import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const TOUR_STATE_KEY = 'teamsly_demo_tour_state';            // 'none' | 'skipped' | 'completed'
const WELCOME_SHOWN_KEY = 'teamsly_demo_welcome_shown';      // sessionStorage flag

const safeGet = (storage, key) => {
    try { return storage.getItem(key); } catch (e) { return null; }
};
const safeSet = (storage, key, val) => {
    try { storage.setItem(key, val); } catch (e) { /* ignore */ }
};

/**
 * useDemoTour - manages welcome card + tour state for demo accounts.
 *
 * Returns { isDemoUser, tourState, welcomeOpen, openWelcome, closeWelcome,
 *           markSkipped, markCompleted, resetTourState }.
 */
export default function useDemoTour(pathname) {
    const isDemoUser = useSelector(s => !!(s.appState && s.appState.demo));
    const [tourState, setTourState] = useState(() => safeGet(localStorage, TOUR_STATE_KEY) || 'none');
    const [welcomeOpen, setWelcomeOpen] = useState(false);

    // Auto-open welcome card once per browser session for demo users
    // who have not yet completed the tour.
    useEffect(() => {
        if (!isDemoUser) return;
        if (tourState === 'completed') return;
        if (safeGet(sessionStorage, WELCOME_SHOWN_KEY) === '1') return;
        safeSet(sessionStorage, WELCOME_SHOWN_KEY, '1');
        setWelcomeOpen(true);
    }, [isDemoUser, tourState]);

    const openWelcome = useCallback(() => setWelcomeOpen(true), []);
    const closeWelcome = useCallback(() => setWelcomeOpen(false), []);

    const markSkipped = useCallback(() => {
        safeSet(localStorage, TOUR_STATE_KEY, 'skipped');
        setTourState('skipped');
    }, []);

    const markCompleted = useCallback(() => {
        safeSet(localStorage, TOUR_STATE_KEY, 'completed');
        setTourState('completed');
    }, []);

    const resetTourState = useCallback(() => {
        safeSet(localStorage, TOUR_STATE_KEY, 'none');
        setTourState('none');
    }, []);

    return {
        isDemoUser,
        tourState,
        welcomeOpen,
        openWelcome,
        closeWelcome,
        markSkipped,
        markCompleted,
        resetTourState,
    };
}
